import { buildExplicitPublicScopeAccess } from '../../../../lib/wrPublicScope';

export type WrPublicSettingRow = {
  option_id?: number | string | null;
  optionId?: number | string | null;
  id?: number | string | null;
  label?: string | null;
  value?: string | null;
  department_id?: number | string | null;
  departmentId?: number | string | null;
  dept_id?: number | string | null;
  visibility?: string | null;
  effective_visibility?: string | null;
  effectiveVisibility?: string | null;
  public?: {
    scope_type?: string | null;
    department_ids?: Array<number | string> | null;
    team_ids?: Array<number | string> | null;
    private_flg?: number | string | null;
    public_department?: string | null;
    public_team?: string | null;
  } | null;
  publish_scope?: {
    scope_type?: string | null;
    department_ids?: Array<number | string> | null;
    team_ids?: Array<number | string> | null;
    private_flg?: number | string | null;
    public_department?: string | null;
    public_team?: string | null;
  } | null;
  publishScope?: {
    scope_type?: string | null;
    department_ids?: Array<number | string> | null;
    team_ids?: Array<number | string> | null;
    private_flg?: number | string | null;
    public_department?: string | null;
    public_team?: string | null;
  } | null;
  private_flg?: number | null;
  scope_type?: string | null;
  department_ids?: Array<number | string> | null;
  team_ids?: Array<number | string> | null;
  public_department?: string | null;
  public_team?: string | null;
};

export type ReportTypeVisibility = 'private' | 'team' | 'dept' | 'public';

export type ReportTypeAccessSetting = {
  visibility: ReportTypeVisibility;
  departments: string[];
  teams: string[];
};

export type ReportTypeOptionLike = {
  id?: number | string | null;
  label?: string | null;
  value?: string | null;
  department_id?: number | string | null;
  departmentId?: number | string | null;
  dept_id?: number | string | null;
  team_id?: number | string | null;
  order_number?: number | string | null;
};

export type ResolveEffectiveAccessForRowArgs<RowLike = any> = {
  row: RowLike;
  reportTypeAccessById: Record<string, ReportTypeAccessSetting> | null | undefined;
  rawReportTypes: ReportTypeOptionLike[];
  deptIdToLabelMap: Record<string, string>;
  reportTypeAccessByLabel?: Record<string, ReportTypeAccessSetting> | null | undefined;
  reportTypeAccessByDeptLabel?: Record<string, ReportTypeAccessSetting> | null | undefined;
  teamsMaster?: any[];
};

export const REPORT_TYPE_VISIBILITY_PRIORITY: Record<ReportTypeVisibility, number> = {
  private: 0,
  team: 10,
  dept: 50,
  public: 100,
};

export const EMPTY_REPORT_TYPE_ACCESS_SETTING: ReportTypeAccessSetting = {
  visibility: 'private',
  departments: [],
  teams: [],
};

export const DEFAULT_REPORT_TYPE_ACCESS_SETTING: ReportTypeAccessSetting = {
  visibility: 'public',
  departments: [],
  teams: [],
};

const toTrimmed = (value: unknown): string => String(value ?? '').trim();

const normalizeReportTypeToken = (value: unknown): string =>
  String(value ?? '').normalize('NFKC').trim();

const toOptionId = (value: unknown): string => toTrimmed(value);

const toDeptId = (option: ReportTypeOptionLike | null | undefined): string =>
  toTrimmed(
    option?.department_id ??
      option?.departmentId ??
      option?.dept_id ??
      ''
  );

const toOptionOrder = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};

const normalizeNumericId = (raw: unknown): string => {
  const source = toTrimmed(raw);
  if (!source) return '';
  if (!/^\d+$/.test(source)) return '';
  const n = Number(source);
  if (!Number.isFinite(n)) return '';
  return String(n);
};

const toReportTypeLabel = (option: ReportTypeOptionLike | null | undefined): string =>
  normalizeReportTypeToken(option?.value ?? option?.label);

const unique = (values: string[]): string[] =>
  Array.from(new Set((values ?? []).map((v) => String(v ?? '').trim()).filter((v) => v.length > 0)));

const mergeUniqueCodes = (a: string[], b: string[]): string[] =>
  unique([...(a ?? []), ...(b ?? [])]);

const matchesReportTypeToken = (option: ReportTypeOptionLike, token: string): boolean => {
  const normalizedToken = normalizeReportTypeToken(token);
  if (!normalizedToken) return false;
  const optionValue = normalizeReportTypeToken(option.value ?? option.label);
  const optionLabel = normalizeReportTypeToken(option.label);
  return optionValue === normalizedToken || optionLabel === normalizedToken;
};

const buildOptionByIdMap = (options: ReportTypeOptionLike[]): Record<string, ReportTypeOptionLike> => {
  const map: Record<string, ReportTypeOptionLike> = {};
  (options ?? []).forEach((option) => {
    const id = toOptionId(option?.id);
    if (!id) return;
    map[id] = option;
  });
  return map;
};

const sortReportTypeOptions = (options: ReportTypeOptionLike[]): ReportTypeOptionLike[] => {
  const collator = new Intl.Collator('ja', { numeric: true, sensitivity: 'base' });
  return [...(options ?? [])].sort((a, b) => {
    const orderDiff = toOptionOrder(a?.order_number) - toOptionOrder(b?.order_number);
    if (orderDiff !== 0) return orderDiff;

    const labelDiff = collator.compare(toReportTypeLabel(a), toReportTypeLabel(b));
    if (labelDiff !== 0) return labelDiff;

    return toOptionId(a?.id).localeCompare(toOptionId(b?.id));
  });
};

export const buildReportTypeOptionsForDisplay = (args: {
  options: ReportTypeOptionLike[];
  selectedDeptIds: Array<string | number>;
  selfDeptId?: string | number | null;
}): ReportTypeOptionLike[] => {
  const options = Array.isArray(args.options) ? args.options : [];
  const selectedDeptIds = new Set(
    unique((args.selectedDeptIds ?? []).map((value) => String(value ?? '').trim()))
  );

  const filtered = options.filter((option) => {
    const id = toOptionId(option?.id);
    if (!id) return false;
    if (selectedDeptIds.size === 0) return true;
    return selectedDeptIds.has(toDeptId(option));
  });

  // No department filter selected: keep each department's option as-is.
  if (selectedDeptIds.size === 0) {
    return sortReportTypeOptions(filtered);
  }

  const groupedByLabel = new Map<string, ReportTypeOptionLike[]>();
  filtered.forEach((option) => {
    const label = toReportTypeLabel(option);
    if (!label) return;
    const current = groupedByLabel.get(label) ?? [];
    current.push(option);
    groupedByLabel.set(label, current);
  });

  const picked: ReportTypeOptionLike[] = [];
  groupedByLabel.forEach((group) => {
    const preferred = pickPreferredReportTypeOption(
      group,
      Array.from(selectedDeptIds),
      args.selfDeptId
    );
    if (!preferred) return;
    picked.push(preferred);
  });

  return sortReportTypeOptions(picked);
};

export const resolveReportTypeOptionIdsByToken = (
  tokenRaw: unknown,
  args: {
    options: ReportTypeOptionLike[];
    preferredIdByLabel?: Map<string, string> | null;
    expandAllWhenUnscoped?: boolean;
  }
): string[] => {
  const token = normalizeReportTypeToken(tokenRaw);
  if (!token) return [];

  const options = Array.isArray(args.options) ? args.options : [];
  const directId = options.find((option) => toOptionId(option?.id) === token);
  if (directId) return [toOptionId(directId?.id)];

  const matched = sortReportTypeOptions(
    options.filter((option) => matchesReportTypeToken(option, token))
  )
    .map((option) => toOptionId(option?.id))
    .filter((id) => id.length > 0);
  if (matched.length === 0) return [];

  if (args.expandAllWhenUnscoped) {
    return unique(matched);
  }

  const preferredId = toTrimmed(args.preferredIdByLabel?.get(token));
  if (preferredId && matched.includes(preferredId)) {
    return [preferredId];
  }

  return [matched[0]];
};

const parseJsonLike = (raw: string): unknown | undefined => {
  const source = String(raw ?? '').trim();
  if (!source) return undefined;
  const first = source[0];
  if (first !== '[' && first !== '{' && first !== '"') return undefined;
  try {
    return JSON.parse(source);
  } catch {
    return undefined;
  }
};

const parseReportTypeTokensFromString = (raw: string): string[] =>
  String(raw ?? '')
    .split(/[\/\uFF0F,\u3001;\uFF1B|\n\r]+/)
    .map((token) => normalizeReportTypeToken(token))
    .filter((token) => token.length > 0);

const parseReportTypeTokens = (raw: unknown): string[] => {
  const walk = (value: unknown): string[] => {
    if (value == null) return [];

    if (Array.isArray(value)) {
      return unique(value.flatMap((entry) => walk(entry)));
    }

    if (typeof value === 'number' || typeof value === 'bigint') {
      return [String(value)];
    }

    if (typeof value === 'string') {
      const source = value.trim();
      if (!source) return [];
      const parsed = parseJsonLike(source);
      if (parsed !== undefined) {
        // JSON文字列がさらに文字列を持つケースにも対応する。
        if (typeof parsed === 'string' && parsed === source) {
          return parseReportTypeTokensFromString(source);
        }
        return walk(parsed);
      }
      return parseReportTypeTokensFromString(source);
    }

    if (typeof value === 'object') {
      const anyValue = value as Record<string, unknown>;
      const preferred = [
        anyValue.attr_value,
        anyValue.attrValue,
        anyValue.value,
        anyValue.values,
        anyValue.label,
        anyValue.name,
        anyValue.text,
      ];

      const preferredTokens = unique(preferred.flatMap((entry) => walk(entry)));
      if (preferredTokens.length > 0) return preferredTokens;

      return unique(Object.values(anyValue).flatMap((entry) => walk(entry)));
    }

    return [];
  };

  return unique(
    walk(raw)
      .map((token) => normalizeReportTypeToken(token))
      .filter((token) => token.length > 0)
  );
};

const REPORT_TYPE_ATTR_NAME = '\u9031\u5831\u5206\u985e';
const REPORT_TYPE_ATTR_NAME_FALLBACK = '\u9a3e\uff71\u8763\uff71\u86fb\u30fb\uff61\u30fb';

const normalizeAttrNameToken = (raw: unknown): string =>
  String(raw ?? '')
    .normalize('NFKC')
    .trim()
    .toLowerCase()
    .replace(/[\s_]/g, '');

const toRowAttributeEntries = (raw: unknown): Array<{
  attr_id?: unknown;
  attr_name?: unknown;
  attr_value?: unknown;
}> => {
  if (raw == null) return [];

  const parsed = typeof raw === 'string' ? parseJsonLike(raw) ?? raw : raw;

  if (Array.isArray(parsed)) {
    return parsed.flatMap((entry) => {
      if (entry == null) return [];
      if (typeof entry !== 'object') {
        return [{ attr_value: entry }];
      }

      const anyEntry = entry as Record<string, unknown>;
      return [
        {
          attr_id:
            anyEntry.attr_id ??
            anyEntry.attrId ??
            anyEntry.id ??
            anyEntry.attribute_id ??
            anyEntry.attributeId,
          attr_name:
            anyEntry.attr_name ??
            anyEntry.attrName ??
            anyEntry.attribute_name ??
            anyEntry.attributeName ??
            anyEntry.name ??
            anyEntry.label,
          attr_value:
            anyEntry.attr_value ??
            anyEntry.attrValue ??
            anyEntry.attribute_value ??
            anyEntry.attributeValue ??
            anyEntry.value ??
            anyEntry.values ??
            anyEntry.text ??
            anyEntry.label ??
            anyEntry.name,
        },
      ];
    });
  }

  if (typeof parsed === 'object') {
    const anyParsed = parsed as Record<string, unknown>;
    const hasAttrShape =
      'attr_id' in anyParsed ||
      'attrId' in anyParsed ||
      'id' in anyParsed ||
      'attribute_id' in anyParsed ||
      'attributeId' in anyParsed ||
      'attr_name' in anyParsed ||
      'attrName' in anyParsed ||
      'attribute_name' in anyParsed ||
      'attributeName' in anyParsed ||
      'attr_value' in anyParsed ||
      'attrValue' in anyParsed ||
      'attribute_value' in anyParsed ||
      'attributeValue' in anyParsed ||
      'value' in anyParsed ||
      'values' in anyParsed;

    if (hasAttrShape) {
      return [
        {
          attr_id:
            anyParsed.attr_id ??
            anyParsed.attrId ??
            anyParsed.id ??
            anyParsed.attribute_id ??
            anyParsed.attributeId,
          attr_name:
            anyParsed.attr_name ??
            anyParsed.attrName ??
            anyParsed.attribute_name ??
            anyParsed.attributeName ??
            anyParsed.name ??
            anyParsed.label,
          attr_value:
            anyParsed.attr_value ??
            anyParsed.attrValue ??
            anyParsed.attribute_value ??
            anyParsed.attributeValue ??
            anyParsed.value ??
            anyParsed.values ??
            anyParsed.text ??
            anyParsed.label ??
            anyParsed.name,
        },
      ];
    }

    return Object.entries(anyParsed).map(([key, value]) => ({
      attr_id: key,
      attr_value: value,
    }));
  }

  return [{ attr_value: parsed }];
};

const isReportTypeAttributeEntry = (entry: {
  attr_id?: unknown;
  attr_name?: unknown;
}): boolean => {
  if (Number(entry?.attr_id) === 1) return true;

  const name = toTrimmed(entry?.attr_name);
  if (!name) return false;
  if (name === REPORT_TYPE_ATTR_NAME || name === REPORT_TYPE_ATTR_NAME_FALLBACK) return true;

  const normalized = normalizeAttrNameToken(name);
  const normalizedMain = normalizeAttrNameToken(REPORT_TYPE_ATTR_NAME);
  const normalizedFallback = normalizeAttrNameToken(REPORT_TYPE_ATTR_NAME_FALLBACK);

  return (
    normalized === normalizedMain ||
    normalized === normalizedFallback ||
    normalized === 'reporttype'
  );
};

const resolveReportTypeTokensFromRow = (row: any): string[] => {
  const attrSources = [
    row?.attributes,
    row?.attribute_values,
    row?.attributeValues,
    row?.attr_values,
    row?.attrValues,
    row?.attrs,
  ];

  const attrs = unique(
    attrSources.flatMap((source) =>
      toRowAttributeEntries(source).map((entry) => JSON.stringify(entry))
    )
  )
    .map((serialized) => {
      const parsed = parseJsonLike(serialized);
      return (parsed ?? {}) as {
        attr_id?: unknown;
        attr_name?: unknown;
        attr_value?: unknown;
      };
    })
    .filter((entry) => entry != null);

  const typedAttrs = attrs.filter(
    (entry) => toTrimmed(entry.attr_id).length > 0 || toTrimmed(entry.attr_name).length > 0
  );

  const reportTypeAttrs = typedAttrs.filter(isReportTypeAttributeEntry);
  let tokens = unique(
    reportTypeAttrs.flatMap((entry) => parseReportTypeTokens(entry.attr_value))
  );

  if (tokens.length === 0 && typedAttrs.length === 0) {
    tokens = unique(attrs.flatMap((entry) => parseReportTypeTokens(entry.attr_value)));
  }

  if (tokens.length > 0) return tokens;

  const directCandidates = [
    row?.report_type,
    row?.reportType,
    row?.report_types,
    row?.reportTypes,
    row?.report_type_name,
    row?.reportTypeName,
    row?.weekly_report_type,
    row?.weeklyReportType,
  ];

  return unique(directCandidates.flatMap((value) => parseReportTypeTokens(value)));
};

const resolveRowDeptCode = (
  row: any,
  deptIdToLabelMap: Record<string, string>,
  teamsMaster: any[]
): string => {
  const rawDeptCandidates = [
    row?.department_code,
    row?.departmentCode,
    row?.department_name,
    row?.department,
  ];

  for (const raw of rawDeptCandidates) {
    const asString = toTrimmed(raw);
    if (!asString) continue;

    const fromId = toTrimmed(deptIdToLabelMap[asString]);
    if (fromId) return normalizeScopeCode(fromId);

    const normalized = normalizeScopeCode(asString);
    if (normalized) return normalized;
  }

  const teamId = toTrimmed(row?.team);
  if (!teamId) return '';

  const teamRow = (teamsMaster ?? []).find((team: any) => {
    const id = toTrimmed(team?.team_id ?? team?.id);
    return id === teamId;
  });
  if (!teamRow) return '';

  const teamDeptId = toTrimmed(
    teamRow?.department_id ?? teamRow?.departmentId ?? teamRow?.dept_id ?? ''
  );
  if (!teamDeptId) return '';

  const byDeptId = toTrimmed(deptIdToLabelMap[teamDeptId]);
  if (byDeptId) return normalizeScopeCode(byDeptId);

  return normalizeScopeCode(teamDeptId);
};

export const normalizeScopeCode = (raw: unknown): string =>
  String(raw ?? '')
    .normalize('NFKC')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

export const parseScopeCodes = (raw: unknown): string[] => {
  const source = String(raw ?? '');
  const normalizedSource = source.normalize('NFKC').toUpperCase();

  const bySplit = normalizedSource
    .split(/[,\uFF0C\u3001/\uFF0F;\uFF1B|\s]+/)
    .map((value) => normalizeScopeCode(value))
    .filter((value) => value.length > 0);

  const byRegex =
    normalizedSource
      .match(/[A-Z]{1,5}[\s_-]*\d{1,4}/g)
      ?.map((value) => normalizeScopeCode(value))
      .filter((value) => value.length > 0) ?? [];

  return unique([...byRegex, ...bySplit]);
};

const normalizeVisibilityToken = (raw: unknown): ReportTypeVisibility | null => {
  const token = String(raw ?? '').trim().toLowerCase();
  if (!token) return null;
  if (token === 'public' || token === 'open' || token === 'all') return 'public';
  if (token === 'team') return 'team';
  if (token === 'dept' || token === 'department' || token === 'partial' || token === 'partial_department') {
    return 'dept';
  }
  if (token === 'private' || token === 'closed' || token === 'nonpublic') return 'private';
  return null;
};

const readPublicScopeFields = (row: WrPublicSettingRow | null | undefined): {
  privateFlgRaw: unknown;
  publicDepartmentRaw: unknown;
  publicTeamRaw: unknown;
} => {
  const scopeLike =
    (row?.public && typeof row.public === 'object' ? row.public : null) ??
    (row?.publish_scope && typeof row.publish_scope === 'object' ? row.publish_scope : null) ??
    (row?.publishScope && typeof row.publishScope === 'object' ? row.publishScope : null);

  return {
    privateFlgRaw: scopeLike?.private_flg ?? row?.private_flg,
    publicDepartmentRaw: scopeLike?.public_department ?? row?.public_department,
    publicTeamRaw: scopeLike?.public_team ?? row?.public_team,
  };
};

const resolveVisibilityFromPrivateFlag = (
  privateFlgRaw: unknown,
  publicDepartmentRaw: unknown,
  publicTeamRaw: unknown
): ReportTypeVisibility | null => {
  const privateFlg = Number(privateFlgRaw);
  if (!Number.isFinite(privateFlg)) return null;
  if (privateFlg === 0) return 'public';

  const teams = parseScopeCodes(publicTeamRaw);
  if (teams.length > 0) return 'team';

  const departments = parseScopeCodes(publicDepartmentRaw);
  if (departments.length > 0) return 'dept';

  if (privateFlg === 2) {
    // Backward compatibility for legacy partial rows with empty scope.
    return 'dept';
  }

  return 'private';
};

export const resolveReportTypeVisibility = (
  row: WrPublicSettingRow | null | undefined
): ReportTypeVisibility => {
  if (!row) return 'private';

  const explicit = buildExplicitPublicScopeAccess(row);
  if (explicit) return explicit.visibility;

  const direct =
    normalizeVisibilityToken(row.visibility) ??
    normalizeVisibilityToken(row.effective_visibility) ??
    normalizeVisibilityToken(row.effectiveVisibility);
  if (direct) return direct;

  const scope = readPublicScopeFields(row);
  return (
    resolveVisibilityFromPrivateFlag(
      scope.privateFlgRaw,
      scope.publicDepartmentRaw,
      scope.publicTeamRaw
    ) ?? 'private'
  );
};

export const resolveDefaultAccessByOption = (
  option: ReportTypeOptionLike | null | undefined,
  deptIdToLabelMap: Record<string, string>
): ReportTypeAccessSetting => {
  const deptId = toDeptId(option);
  if (!deptId) return EMPTY_REPORT_TYPE_ACCESS_SETTING;

  const label = toTrimmed(deptIdToLabelMap[deptId]);
  const normalized = normalizeScopeCode(label || deptId);
  if (!normalized) return EMPTY_REPORT_TYPE_ACCESS_SETTING;

  return {
    visibility: 'dept',
    departments: [normalized],
    teams: [],
  };
};

export const buildAccessSettingFromWrPublicSetting = (
  row: WrPublicSettingRow | null | undefined,
  opts?: { fallbackDeptCode?: string | null }
): ReportTypeAccessSetting => {
  const explicit = buildExplicitPublicScopeAccess(row);
  if (explicit) return explicit;

  const scope = readPublicScopeFields(row);
  const visibility = resolveReportTypeVisibility(row);
  const departments = parseScopeCodes(scope.publicDepartmentRaw);
  const teams = parseScopeCodes(scope.publicTeamRaw);

  const privateFlg = Number(scope.privateFlgRaw);
  const fallbackDeptCode = normalizeScopeCode(opts?.fallbackDeptCode ?? '');

  // private_flg=1 and no scope should still be visible to the option's own department.
  if (
    privateFlg === 1 &&
    visibility === 'private' &&
    departments.length === 0 &&
    teams.length === 0 &&
    fallbackDeptCode.length > 0
  ) {
    return {
      visibility: 'dept',
      departments: [fallbackDeptCode],
      teams: [],
    };
  }

  return { visibility, departments, teams };
};

export const parseWrPublicSettingOptionIds = (
  raw: unknown,
  knownIds?: ReadonlySet<string>
): string[] => {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return unique(
      raw.flatMap((value) => parseWrPublicSettingOptionIds(value, knownIds))
    );
  }
  if (typeof raw === 'number' || typeof raw === 'bigint') {
    const id = String(raw);
    if (!id) return [];
    if (knownIds && knownIds.size > 0 && !knownIds.has(id)) return [];
    return [id];
  }

  const source = String(raw ?? '').trim();
  if (!source) return [];

  const parts = source
    .split(/[,\uFF0C\u3001/\uFF0F;\uFF1B\s|]+/)
    .map((value) => String(value ?? '').trim())
    .filter((value) => /^\d+$/.test(value));

  const regexNumbers =
    source
      .match(/\d+/g)
      ?.map((value) => String(value ?? '').trim())
      .filter((value) => /^\d+$/.test(value)) ?? [];

  const joined = source.replace(/[,\uFF0C\u3001/\uFF0F;\uFF1B\s|]/g, '').trim();
  const hasKnownIds = Boolean(knownIds && knownIds.size > 0);

  if (!hasKnownIds) {
    const merged = unique([...parts, ...regexNumbers]);
    if (/^\d+$/.test(joined) && !merged.includes(joined)) {
      const looksThousands =
        /^\d{1,3}(,\d{3})+$/.test(source) || /^\d{1,3}(\uFF0C\d{3})+$/.test(source);
      if (looksThousands || merged.length === 0) merged.push(joined);
    }
    return unique(merged);
  }

  const known = knownIds as ReadonlySet<string>;
  if (known.has(source)) return [source];

  const knownParts = unique([...parts, ...regexNumbers]).filter((id) => known.has(id));
  const joinedKnown = /^\d+$/.test(joined) && known.has(joined);
  const looksThousands =
    /^\d{1,3}(,\d{3})+$/.test(source) || /^\d{1,3}(\uFF0C\d{3})+$/.test(source);

  if (knownParts.length > 0 && !joinedKnown) return unique(knownParts);
  if (joinedKnown && knownParts.length === 0) return [joined];
  if (joinedKnown && knownParts.length > 0) {
    if (looksThousands) return [joined];
    return unique(knownParts);
  }

  const fallback = unique([...parts, ...regexNumbers]);
  if (/^\d+$/.test(joined) && !fallback.includes(joined)) fallback.push(joined);
  return unique(fallback).filter((id) => known.has(id));
};

const resolveOptionIdsForPublicSettingRow = (
  row: WrPublicSettingRow,
  options: ReportTypeOptionLike[],
  knownIds?: ReadonlySet<string>
): string[] => {
  const idsFromFields = unique(
    [
      row?.option_id,
      row?.optionId,
      row?.id,
    ].flatMap((value) => parseWrPublicSettingOptionIds(value, knownIds))
  );
  if (idsFromFields.length > 0) return idsFromFields;

  const anyRow = row as any;
  const labelTokens = unique(
    [
      anyRow?.value,
      anyRow?.label,
      anyRow?.option_label,
      anyRow?.optionLabel,
      anyRow?.option_name,
      anyRow?.optionName,
    ]
      .map((value) => toTrimmed(value))
      .filter((value) => value.length > 0)
  );
  if (labelTokens.length === 0) return [];

  const deptCandidates = unique(
    [
      row?.department_id,
      row?.departmentId,
      row?.dept_id,
    ]
      .map((value) => toTrimmed(value))
      .filter((value) => value.length > 0)
  );

  const matchedIds = unique(
    (options ?? [])
      .filter((option) =>
        labelTokens.some((token) => matchesReportTypeToken(option, token))
      )
      .filter((option) => {
        if (deptCandidates.length === 0) return true;
        const deptId = toDeptId(option);
        return deptId.length > 0 && deptCandidates.includes(deptId);
      })
      .map((option) => toOptionId(option?.id))
      .filter((id) => id.length > 0)
  );

  if (!knownIds || knownIds.size === 0) return matchedIds;
  return matchedIds.filter((id) => knownIds.has(id));
};

const resolveDeptIdFromPublicSettingRow = (row: WrPublicSettingRow): string =>
  toTrimmed(row?.department_id ?? row?.departmentId ?? row?.dept_id ?? '');

const buildReportTypeTokenCandidatesFromRow = (
  row: WrPublicSettingRow | null | undefined
): string[] => {
  const anyRow = row as any;
  return unique(
    [
      anyRow?.value,
      anyRow?.label,
      anyRow?.option_label,
      anyRow?.optionLabel,
      anyRow?.option_name,
      anyRow?.optionName,
    ]
      .map((value) => normalizeReportTypeToken(value))
      .filter((value) => value.length > 0)
  );
};

const buildReportTypeTokenCandidatesFromOptions = (
  optionIds: string[],
  optionById: Record<string, ReportTypeOptionLike>
): string[] =>
  unique(
    optionIds.flatMap((optionId) => {
      const option = optionById[optionId];
      if (!option) return [];
      return [
        normalizeReportTypeToken(option.value ?? option.label),
        normalizeReportTypeToken(option.label),
      ].filter((value) => value.length > 0);
    })
  );

const buildDeptTokenLookupKey = (deptId: unknown, token: unknown): string => {
  const normalizedDeptId = toTrimmed(deptId);
  const normalizedToken = normalizeReportTypeToken(token);
  if (!normalizedDeptId || !normalizedToken) return '';
  return `${normalizedDeptId}::${normalizedToken}`;
};

const resolveDepartmentIdFromValue = (
  raw: unknown,
  deptIdToLabelMap: Record<string, string>
): string => {
  const source = toTrimmed(raw);
  if (!source) return '';

  const direct = normalizeNumericId(source);
  if (direct) return direct;

  const exactEntry = Object.entries(deptIdToLabelMap).find(
    ([, label]) => toTrimmed(label) === source
  );
  if (exactEntry) {
    return normalizeNumericId(exactEntry[0]);
  }

  const normalizedSource = normalizeScopeCode(source);
  if (!normalizedSource) return '';
  const normalizedEntry = Object.entries(deptIdToLabelMap).find(
    ([, label]) => normalizeScopeCode(label) === normalizedSource
  );
  if (!normalizedEntry) return '';
  return normalizeNumericId(normalizedEntry[0]);
};

const resolveRowDepartmentIdForMatching = (
  row: any,
  deptIdToLabelMap: Record<string, string>
): string => {
  const candidates = [
    row?.department,
    row?.department_id,
    row?.departmentId,
    row?.dept,
    row?.dept_id,
    row?.deptId,
    row?.division,
    row?.division_id,
    row?.divisionId,
  ];

  for (const candidate of candidates) {
    const resolved = resolveDepartmentIdFromValue(candidate, deptIdToLabelMap);
    if (resolved) return resolved;
  }
  return '';
};

const resolveRowTeamIdForMatching = (row: any): string => {
  const candidates = [
    row?.team,
    row?.team_id,
    row?.teamId,
    row?.team_code,
    row?.teamCode,
  ];
  for (const candidate of candidates) {
    const resolved = normalizeNumericId(candidate);
    if (resolved) return resolved;
  }
  return '';
};

export const compareAccessSettingWidth = (
  a: ReportTypeAccessSetting,
  b: ReportTypeAccessSetting
): number => {
  const ap = REPORT_TYPE_VISIBILITY_PRIORITY[a.visibility];
  const bp = REPORT_TYPE_VISIBILITY_PRIORITY[b.visibility];
  if (ap !== bp) return ap - bp;

  if (a.visibility === 'dept') return a.departments.length - b.departments.length;
  if (a.visibility === 'team') return a.teams.length - b.teams.length;
  return 0;
};

export const pickWiderAccessSetting = (
  current: ReportTypeAccessSetting,
  candidate: ReportTypeAccessSetting
): ReportTypeAccessSetting => {
  const cmp = compareAccessSettingWidth(candidate, current);
  if (cmp > 0) return candidate;
  if (cmp < 0) return current;
  if (candidate.visibility !== current.visibility) return current;
  if (candidate.visibility !== 'dept' && candidate.visibility !== 'team') return current;

  return {
    visibility: current.visibility,
    departments: mergeUniqueCodes(current.departments, candidate.departments),
    teams: mergeUniqueCodes(current.teams, candidate.teams),
  };
};

export const canAccessByUserDep = (
  setting: ReportTypeAccessSetting,
  userDepRaw: string
): boolean => {
  const userDepCandidates = unique([
    normalizeScopeCode(userDepRaw),
    ...String(userDepRaw ?? '')
      .normalize('NFKC')
      .toUpperCase()
      .match(/[A-Z]{1,5}\d{1,4}/g) ?? [],
  ]);
  let result = false;

  if (setting.visibility === 'public') {
    result = true;
  } else if (setting.visibility === 'private') {
    result = false;
  } else if (userDepCandidates.length === 0) {
    result = false;
  } else if (setting.visibility === 'dept') {
    result = userDepCandidates.some((dep) => setting.departments.includes(dep));
  } else if (setting.visibility === 'team') {
    // Team code may be unavailable on the client; keep department scope as fallback.
    result = userDepCandidates.some(
      (dep) => setting.teams.includes(dep) || setting.departments.includes(dep)
    );
  } else {
    result = false;
  }

  return result;
};

export const buildReportTypeAccessMaps = (args: {
  rows: WrPublicSettingRow[];
  options: ReportTypeOptionLike[];
  deptIdToLabelMap: Record<string, string>;
  knownOptionIds?: ReadonlySet<string>;
}): {
  accessById: Record<string, ReportTypeAccessSetting>;
  visibilityById: Record<string, ReportTypeVisibility>;
  accessByLabel: Record<string, ReportTypeAccessSetting>;
  accessByDeptLabel: Record<string, ReportTypeAccessSetting>;
} => {
  const rows = Array.isArray(args.rows) ? args.rows : [];
  const options = Array.isArray(args.options) ? args.options : [];
  const optionById = buildOptionByIdMap(options);
  const fallbackKnown = new Set(Object.keys(optionById));
  const knownIds = args.knownOptionIds && args.knownOptionIds.size > 0
    ? args.knownOptionIds
    : fallbackKnown;

  const accessById: Record<string, ReportTypeAccessSetting> = {};
  const explicitAccessById: Record<string, ReportTypeAccessSetting> = {};
  const accessByLabel: Record<string, ReportTypeAccessSetting> = {};
  const accessByDeptLabel: Record<string, ReportTypeAccessSetting> = {};

  rows.forEach((row) => {
    const optionIds = resolveOptionIdsForPublicSettingRow(row, options, knownIds);
    const rowDeptId = resolveDeptIdFromPublicSettingRow(row);
    const fallbackFromRowDept =
      rowDeptId.length > 0
        ? normalizeScopeCode(args.deptIdToLabelMap[rowDeptId] ?? rowDeptId)
        : '';
    const fallbackFromOptions = optionIds
      .map((optionId) => {
        const option = optionById[optionId];
        if (!option) return '';
        return resolveDefaultAccessByOption(option, args.deptIdToLabelMap).departments[0] ?? '';
      })
      .find((value) => value.length > 0) ?? '';

    const setting = buildAccessSettingFromWrPublicSetting(row, {
      fallbackDeptCode: fallbackFromOptions || fallbackFromRowDept,
    });

    if (optionIds.length > 0) {
      optionIds.forEach((optionId) => {
        const current = explicitAccessById[optionId];
        explicitAccessById[optionId] = current
          ? pickWiderAccessSetting(current, setting)
          : setting;
      });
    }

    const tokens = unique([
      ...buildReportTypeTokenCandidatesFromRow(row),
      ...buildReportTypeTokenCandidatesFromOptions(optionIds, optionById),
    ]);
    if (tokens.length === 0) return;

    tokens.forEach((token) => {
      const current = accessByLabel[token];
      accessByLabel[token] = current
        ? pickWiderAccessSetting(current, setting)
        : setting;

      const deptTokenKey = buildDeptTokenLookupKey(rowDeptId, token);
      if (!deptTokenKey) return;
      const currentByDept = accessByDeptLabel[deptTokenKey];
      accessByDeptLabel[deptTokenKey] = currentByDept
        ? pickWiderAccessSetting(currentByDept, setting)
        : setting;
    });
  });

  Object.keys(optionById).forEach((optionId) => {
    accessById[optionId] = explicitAccessById[optionId] ?? DEFAULT_REPORT_TYPE_ACCESS_SETTING;
  });
  Object.keys(explicitAccessById).forEach((optionId) => {
    if (!Object.prototype.hasOwnProperty.call(accessById, optionId)) {
      accessById[optionId] = explicitAccessById[optionId];
    }
  });

  const visibilityById: Record<string, ReportTypeVisibility> = {};
  Object.entries(accessById).forEach(([optionId, setting]) => {
    visibilityById[optionId] = setting.visibility;
  });

  return { accessById, visibilityById, accessByLabel, accessByDeptLabel };
};

export const dedupeRowsByWrId = <T,>(rows: T[]): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];

  (rows ?? []).forEach((row) => {
    const wrId = row && typeof row === 'object' ? (row as any)?.wr_id : undefined;
    const key = toTrimmed(wrId);
    if (!key) {
      out.push(row);
      return;
    }
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });

  return out;
};

export const pickPreferredReportTypeOption = (
  options: ReportTypeOptionLike[],
  preferredDeptIdsRaw: Array<string | number>,
  selfDeptIdRaw?: string | number | null
): ReportTypeOptionLike | null => {
  if (!Array.isArray(options) || options.length === 0) return null;

  const preferredDeptIds = new Set(
    unique((preferredDeptIdsRaw ?? []).map((value) => String(value ?? '').trim()))
  );
  const selfDeptId = toTrimmed(selfDeptIdRaw);

  const scoreOf = (option: ReportTypeOptionLike): number => {
    const deptId = toDeptId(option);
    if (deptId && preferredDeptIds.has(deptId)) return 200;
    if (preferredDeptIds.size === 0 && selfDeptId && deptId === selfDeptId) return 100;
    return 0;
  };

  const sorted = [...options].sort((a, b) => {
    const scoreDiff = scoreOf(b) - scoreOf(a);
    if (scoreDiff !== 0) return scoreDiff;

    const orderDiff = toOptionOrder(a.order_number) - toOptionOrder(b.order_number);
    if (orderDiff !== 0) return orderDiff;

    return toOptionId(a.id).localeCompare(toOptionId(b.id));
  });

  return sorted[0] ?? null;
};

export const buildPreferredReportTypeIdByLabel = (
  options: ReportTypeOptionLike[],
  preferredDeptIds: Array<string | number>,
  selfDeptId?: string | number | null
): Map<string, string> => {
  const grouped = new Map<string, ReportTypeOptionLike[]>();

  (options ?? []).forEach((option) => {
    const label = normalizeReportTypeToken(option.value ?? option.label);
    const id = toOptionId(option.id);
    if (!label || !id) return;

    const current = grouped.get(label) ?? [];
    current.push(option);
    grouped.set(label, current);
  });

  const out = new Map<string, string>();
  grouped.forEach((group, label) => {
    const picked = pickPreferredReportTypeOption(group, preferredDeptIds, selfDeptId);
    const id = toOptionId(picked?.id);
    if (!id) return;
    out.set(label, id);
  });

  return out;
};

export const resolveEffectiveAccessForRow = <RowLike = any>(
  args: ResolveEffectiveAccessForRowArgs<RowLike>
): ReportTypeAccessSetting => {
  const row = args.row as any;
  const tokens = resolveReportTypeTokensFromRow(row);
  if (tokens.length === 0) return DEFAULT_REPORT_TYPE_ACCESS_SETTING;

  const rowDeptId = resolveRowDepartmentIdForMatching(row, args.deptIdToLabelMap);
  const rowTeamId = resolveRowTeamIdForMatching(row);
  const accessMap = args.reportTypeAccessById ?? {};
  const accessByLabel = args.reportTypeAccessByLabel ?? {};
  const accessByDeptLabel = args.reportTypeAccessByDeptLabel ?? {};

  let bestSetting = EMPTY_REPORT_TYPE_ACCESS_SETTING;

  tokens.forEach((token) => {
    const byTeam = (args.rawReportTypes ?? []).filter((option) => {
      if (!rowDeptId || !rowTeamId) return false;
      const optionDeptId = normalizeNumericId(option.department_id);
      if (!optionDeptId || optionDeptId !== rowDeptId) return false;
      const optionTeamId = normalizeNumericId(option.team_id);
      if (!optionTeamId || optionTeamId !== rowTeamId) return false;
      return matchesReportTypeToken(option, token);
    });
    const byDept = (args.rawReportTypes ?? []).filter((option) => {
      if (!rowDeptId) return false;
      const optionDeptId = normalizeNumericId(option.department_id);
      if (!optionDeptId || optionDeptId !== rowDeptId) return false;
      return matchesReportTypeToken(option, token);
    });

    const sourceCandidates = byTeam.length > 0 ? byTeam : byDept;

    const candidates = Array.from(
      new Map(
        sourceCandidates.map((option) => [toOptionId(option?.id), option])
      ).values()
    ).filter((option) => toOptionId(option?.id).length > 0);

    let tokenSetting = EMPTY_REPORT_TYPE_ACCESS_SETTING;
    const resolved = candidates
      .map((option) => {
        const optionId = toOptionId(option?.id);
        if (!optionId) return null;

        const defaultSetting = DEFAULT_REPORT_TYPE_ACCESS_SETTING;
        return {
          configured: Object.prototype.hasOwnProperty.call(accessMap, optionId),
          setting: accessMap[optionId] ?? defaultSetting,
        };
      })
      .filter((value): value is { configured: boolean; setting: ReportTypeAccessSetting } => value != null);

    const configuredResolved = resolved.filter((value) => value.configured);
    const effectiveResolved = configuredResolved.length > 0 ? configuredResolved : resolved;

    effectiveResolved.forEach((value) => {
      tokenSetting = pickWiderAccessSetting(tokenSetting, value.setting);
    });

    const deptTokenKey = buildDeptTokenLookupKey(rowDeptId, token);
    const fromDeptLabel = deptTokenKey ? accessByDeptLabel[deptTokenKey] : undefined;
    if (fromDeptLabel) {
      tokenSetting = pickWiderAccessSetting(tokenSetting, fromDeptLabel);
    }

    const fromLabel = accessByLabel[normalizeReportTypeToken(token)];
    if (fromLabel && effectiveResolved.length === 0 && !fromDeptLabel) {
      tokenSetting = pickWiderAccessSetting(tokenSetting, fromLabel);
    }

    bestSetting = pickWiderAccessSetting(bestSetting, tokenSetting);
  });

  if (
    bestSetting.visibility === 'private' &&
    bestSetting.departments.length === 0 &&
    bestSetting.teams.length === 0
  ) {
    const rowDeptCode = resolveRowDeptCode(
      row,
      args.deptIdToLabelMap,
      Array.isArray(args.teamsMaster) ? args.teamsMaster : []
    );
    if (rowDeptCode) {
      return {
        visibility: 'dept',
        departments: [rowDeptCode],
        teams: [],
      };
    }
  }

  return bestSetting;
};
